import { makeAutoObservable, reaction, toJS } from "mobx";
import _ from "lodash";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { tokenManager } from "../../../api/tokenManager";
import { UpdateEntityStore } from "../../common/store/types";
import { newIdempotencyKey, requestFingerprint } from "../../../common/utils";
import { userService } from "../services/userService";
import {
  UserValuesInit,
  getUserUpdateVariables,
  getUserValues,
  UserValuesType,
  UserErrorsType,
  userDisplayId,
} from "./UserCommonStore";
import {
  CustomValidator,
  runCustomValidators,
  collectCustomWarnings,
} from "../../common/validation/types";
import * as customValidations from "./customValidations";
import { User } from "../../../api/graphql";
import { resolveCurrentLang } from "../../../common/utils";
import {
  getRoleValues,
  RoleValuesType,
  roleDisplayId,
} from "../../role/store/RoleCommonStore";
import { addedStatusList } from "../../common/types";
import { imageCache } from "../../../common/services/imageCache";
import { formatBytes } from "../../../common/utils";
import { isNil } from "../../../common/utils";
import { validateMask } from "../../common/utils";
import { uploadFailureReason } from "../../../common/utils";
import { apiUploadFile } from "../../../api/apiClient";
import {
  downloadWithAuth,
  fetchFileBlob,
} from "../../../common/utils/downloadWithAuth";
import { Language } from "../../../api/graphql";
import { IdCardType } from "../../../api/graphql";
import { Gender } from "../../../api/graphql";
import { roleService } from "../../role/services/roleService";
import { InitializeParams } from "../../../store/types";

type AsyncFunction = () => Promise<any>;

export class UserUpdateStore implements UpdateEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  validationErrorsDialogOpen: boolean = false;
  confirmDialogOpened: boolean = false;
  confirmDialogTitle: string = "";
  confirmDialogDescription: string = "";
  confirmDialogCallback: (() => void) | null = null;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  errors: UserErrorsType = {};
  service = userService;
  userInstance: User | null = null;
  cardState: Record<string, { open: boolean; errors: string[] }> = {
    null: { open: true, errors: [] },
    main: { open: true, errors: [] },
    details: { open: true, errors: [] },
  };
  navigationLinks: { id: string; label: string; errors: string }[] = [];
  savedId: string | null = null;
  // Mientras guarda (envío y relectura): la capa del formulario (SavingOverlay)
  // lo bloquea, así nada de lo que se escribiera lo pisa la relectura.
  isSaving: boolean = false;
  // Se guardó, pero el formulario no se pudo poner al día (la relectura falló, o
  // el formulario cambió mientras se guardaba): Guardar queda deshabilitado hasta
  // recargar (OutdatedAfterSaveNotice), así nunca se guarda desde un estado viejo.
  savedButOutdated: boolean = false;
  // En ese estado, lo que se escribe cuenta como cambio pendiente: la foto del
  // formulario cubre los campos; este vigía, también las grillas y los archivos.
  private editedWhileOutdated: boolean = false;
  private stopOutdatedWatch?: () => void;
  cancelled = false;
  callbackParams: CallbackDataParams | null = null;
  entityValues = UserValuesInit();
  nameError: string | undefined;
  emailError: string | undefined;
  roleError: string | undefined;
  roleService = roleService;
  roleSearchValue: string = "";
  roleOptions: RoleValuesType[] = [];
  languageError: string | undefined;
  avatarError: string | undefined;
  avatarPreviewUrl: string | null = null;
  activeError: string | undefined;
  cellphoneError: string | undefined;
  twoFaseAuthError: string | undefined;
  idCardTypeError: string | undefined;
  idCardError: string | undefined;
  genderError: string | undefined;
  pdfViewerOpen: boolean = false;
  pdfViewerUrl: string = "";
  pdfViewerTitle: string = "";

  private customValidators: CustomValidator<UserValuesType>[] = [];
  private _ownPendingChanges = false;
  // La clave de idempotencia del guardado en curso y lo que mandó: un reintento
  // que manda lo mismo (sin cambios desde el intento que falló) la reusa, y el
  // server lo reconoce si ya lo había aplicado; cualquier cambio, o un guardado
  // exitoso, la renueva.
  private saveAttempt: { key: string; request: string } | null = null;

  private idempotencyKeyFor(variables: object): string {
    const request = requestFingerprint(variables);
    if (!this.saveAttempt || this.saveAttempt.request !== request) {
      this.saveAttempt = { key: newIdempotencyKey(), request };
    }
    return this.saveAttempt.key;
  }

  private originalSnapshot: Record<string, any> | null = null;
  private stopReaction?: () => void;
  originalState: any = null;
  // Callback system for "Add new" flow
  isWaitingForCallback = false;
  callbackReturnPath: string | null = null;
  // Modal mode flag - when true, reference views open as stacked modals
  isModalMode = false;
  // Drawer mode flag - when true, close() pops/closes the drawer instead of navigating
  isDrawerMode = false;
  // Handler for inline create dialog — when set, close() calls this instead of navigating
  closeHandler:
    | ((returnData?: { resultId?: string; cancelled?: boolean }) => void)
    | null = null;
  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    this.setNavigationLinks();

    // Auto-registra los validators custom exportados por customValidations.ts
    // (stub editable del proyecto). Evita tener que editar este store generado.
    Object.values(customValidations).forEach((v) => {
      if (v && typeof (v as any).validate === "function") {
        this.registerCustomValidator(v as CustomValidator<UserValuesType>);
      }
    });
    makeAutoObservable(this, {
      originalState: false,
    });
    this.setupChangeDetection();
  }

  destroy() {
    // Primero, lejos de lo que un proyecto agrega después del bloque de stopReaction.
    this.stopOutdatedWatch?.();
    if (this.stopReaction) {
      this.stopReaction();
    }
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  registerCustomValidator(validator: CustomValidator<UserValuesType>) {
    this.customValidators.push(validator);
  }

  // Acceso resiliente a cardState: un card referenciado por un form pero no
  // inicializado (p.ej. un card manual del proyecto) NO debe crashear la página.
  // Los getters son puros (no mutan durante el render); los mutadores auto-crean
  // la entrada faltante con un default abierto y sin errores.
  toggleCardState(id: string) {
    if (!this.cardState[id]) this.cardState[id] = { open: true, errors: [] };
    this.cardState[id].open = !this.cardState[id].open;
  }

  setCardState(id: string, status: boolean) {
    if (!this.cardState[id]) this.cardState[id] = { open: true, errors: [] };
    this.cardState[id].open = status;
  }

  getCardState(id: string) {
    return this.cardState[id]?.open ?? true;
  }

  getCardErrors(id: string) {
    return this.cardState[id]?.errors?.join(", ") ?? "";
  }

  *loadUserById(id: string): any {
    const validToken = yield tokenManager.getValidAccessToken();
    if (!validToken) {
      throw new Error("SESSION_EXPIRED");
    }
    this.rootStore.authStore.accessToken = validToken;
    this.userInstance = yield this.service.getOne(
      id,
      validToken,
      this.rootStore.activityStore
    );
    if (this.userInstance) {
      // Lo que se ve es lo que hay en el server: el formulario está al día.
      this.becomeCurrent();
      this.entityValues = getUserValues(this.userInstance, this.langStore);
      this.saveOriginalState();
    }
  }

  setCardError(id: string, message: string) {
    const state = this.cardState[id] ?? { open: true, errors: [] };
    this.cardState = {
      ...this.cardState,
      ...{
        [id]: { open: state.open, errors: [...state.errors, message] },
      },
    };
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get isProcessing() {
    return this.status === "processing";
  }

  // Guardar deshabilitado: mientras se guarda, y con el formulario guardado pero desactualizado.
  get saveDisabled() {
    return this.isProcessing || this.savedButOutdated;
  }

  /**
   * Vuelve a leer el registro: la acción del aviso de guardado pero desactualizado.
   * Si la lectura sale bien el formulario queda al día y Guardar se vuelve a
   * habilitar; si no, el error queda a la vista y el aviso sigue.
   */
  *reloadUser(): any {
    if (this.isProcessing) return;
    if (this.pendingChanges) {
      // Lo escrito después de quedar desactualizado se descartaría: se pregunta,
      // como al salir.
      this.showConfirmDialog(
        this.langStore.confirmCancelTitle,
        this.langStore.reloadWithoutSaving,
        this.executeReloadAction.bind(this)
      );
      return;
    }
    yield this.executeReloadAction();
  }

  *executeReloadAction(): any {
    yield this.process(() => this.loadUserById(this.entityValues.id));
  }

  /**
   * Queda guardado pero desactualizado. Se toma la foto del formulario: lo editado
   * antes de guardar ya se guardó y no cuenta; lo que se escriba desde ahora cuenta
   * como cambio pendiente, y Cancelar, la navegación y Recargar avisan antes de
   * descartarlo. Para lo que la foto no cubre (las grillas y los archivos), el
   * vigía lo marca.
   */
  private becomeOutdated() {
    this.savedButOutdated = true;
    this.resetPendingChanges();
    this.saveOriginalState();
    this.editedWhileOutdated = false;
    this.stopOutdatedWatch?.();
    this.stopOutdatedWatch = reaction(
      () => ({ ...toJS(this.entityValues), version_: undefined }),
      () => {
        this.editedWhileOutdated = true;
      },
      { equals: (a, b) => _.isEqual(a, b) }
    );
  }

  /** El formulario está al día (se volvió a leer el registro): se sale del estado. */
  private becomeCurrent() {
    this.stopOutdatedWatch?.();
    this.stopOutdatedWatch = undefined;
    this.editedWhileOutdated = false;
    this.savedButOutdated = false;
  }

  get displayId(): string {
    if (this._ownPendingChanges && this.entityValues) {
      return userDisplayId(this.entityValues as any, this.langStore);
    }
    if (this.userInstance) {
      return userDisplayId(this.userInstance, this.langStore);
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.UserSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  showValidationErrorsDialog() {
    this.validationErrorsDialogOpen = true;
  }

  closeValidationErrorsDialog() {
    this.validationErrorsDialogOpen = false;
  }

  setContentTop(_contentTop: number) {}

  setNavigationLinks() {
    this.navigationLinks = [
      {
        id: "main",
        label: this.rootStore.langStore.groupUserMainLabel,
        errors: this.getCardErrors("main"),
      },
      {
        id: "details",
        label: this.rootStore.langStore.groupUserDetailsLabel,
        errors: this.getCardErrors("details"),
      },
    ];
  }

  *returnedData(data: any): any {
    const { callerData, returnData } = data;
    if (
      callerData &&
      returnData &&
      !returnData.cancelled &&
      (returnData.entity || returnData.resultId)
    ) {
      // Delegate to subentity UpdateStore if callerData.entityName matches a subentity

      if (callerData?.fieldName === "role") {
        if (returnData.entity) {
          const displayId =
            returnData.entity.displayId ||
            roleDisplayId(returnData.entity, this.langStore);
          this.entityValues.showRole = { id: returnData.entity.id, displayId };
        } else if (returnData.resultId) {
          const api = yield* getApi(
            this.rootStore.authStore,
            this.rootStore.activityStore
          );
          const result = yield api.roleOne({ id: returnData.resultId });
          const role = result.roleOne;
          this.entityValues.showRole = {
            displayId: role.displayId,
            id: role.id,
          };
          this.setRoleOptions([getRoleValues(role, this.langStore)]);
        }
      }
    }
  }

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  showConfirmDialog(title: string, description: string, callback: () => void) {
    this.confirmDialogTitle = title;
    this.confirmDialogDescription = description;
    this.confirmDialogCallback = callback;
    this.confirmDialogOpened = true;
  }

  closeConfirmDialog() {
    this.confirmDialogOpened = false;
    this.confirmDialogCallback = null;
  }

  *executeConfirmedAction() {
    const callback = this.confirmDialogCallback;
    this.closeConfirmDialog();
    if (callback) {
      yield callback();
    }
  }

  getNameValue(): string {
    return this.entityValues.name;
  }

  setNameValue(value: string) {
    this.entityValues.name = value;
  }

  getEmailValue(): string {
    return this.entityValues.email;
  }

  setEmailValue(value: string) {
    this.entityValues.email = value;
  }

  setRoleOptions(values: RoleValuesType[]) {
    this.roleOptions = values;
  }

  *loadRoleValues(): any {
    let where: any = null;
    if (this.roleSearchValue) {
      const searchFilter = {
        [this.langStore.fieldByLang("name")]: {
          contains: this.roleSearchValue,
          mode: "insensitive",
        },
      };
      where = where ? { AND: [where, searchFilter] } : searchFilter;
    }
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.roleListWithOffset({
      offset: 0,
      take: 100,
      where: where,
    });
    this.setRoleOptions(
      (result.roleListWithOffset || []).map((x: any) =>
        getRoleValues(x, this.langStore)
      )
    );
  }

  getRoleOptions() {
    return this.roleOptions.map((x: any) => {
      return {
        displayId: resolveCurrentLang(x.displayId, this.langStore),
        id: x.id,
      };
    });
  }

  *setRoleValue(value: string) {
    this.roleSearchValue = value;
    yield this.loadRoleValues();
  }

  // eslint-disable-next-line require-yield -- extension point: users may inject `yield this.helper(...)` after generation; see workAuthor.copyAuthorPublishersToWorkAuthor
  *setSelectedRole(value: { displayId: string; id: string } | null) {
    if (value?.id === "add-new") {
      const notFound = /".*?"/.exec(value.displayId);
      this.addNewRole(
        notFound ? notFound[0].replaceAll('"', "") : undefined,
        "role"
      );
    } else {
      if (value) {
        this.entityValues.showRole = value;
        this.entityValues.__role = value.displayId;
      }
    }
  }

  getLanguageValue(): Language {
    return this.entityValues.language;
  }

  setLanguageValue(value: Language) {
    this.entityValues.language = value;
  }

  getLanguageOptions() {
    return this.langStore.LanguageOptions;
  }

  *avatarUploadBlob(blob: Blob): any {
    const previousFiles = [...this.entityValues.__avatar_files];
    const previousName = this.entityValues.avatar;
    // Crear preview URL para mostrar inmediatamente
    if (this.avatarPreviewUrl) {
      URL.revokeObjectURL(this.avatarPreviewUrl);
    }
    this.avatarPreviewUrl = URL.createObjectURL(blob);

    const file = new File([blob], "avatar.jpg", {
      type: blob.type || "image/jpeg",
    });
    const fileId = `avatar_${Date.now()}`;
    const newFile = {
      __status__: "added" as const,
      id: fileId,
      name: file.name,
      size: file.size,
      formattedSize: formatBytes(file.size),
      mimetype: file.type,
      ticket: "",
    };
    this.entityValues.__avatar_files = [newFile];
    this.entityValues.avatar = file.name;

    const outcome = yield this.uploadTicket(file);
    if (!outcome.ticket) {
      // La subida no terminó: el servidor no tiene el archivo, así que no
      // puede quedar en la lista. Vuelve lo que había y el campo dice por qué.
      this.entityValues.__avatar_files = previousFiles;
      this.entityValues.avatar = previousName;
      if (this.avatarPreviewUrl) {
        URL.revokeObjectURL(this.avatarPreviewUrl);
        this.avatarPreviewUrl = null;
      }
      if (outcome.cancelled) {
        // Lo canceló el usuario desde el modal de espera: no es una falla.
        this.clearFieldError("avatar");
      } else {
        this.setFieldError(
          "avatar",
          this.langStore.UserAvatarLabel,
          this.langStore.uploadFailedMessage(file.name, outcome.reason)
        );
      }
      return;
    }
    this.clearFieldError("avatar");
    const fileIndex = this.entityValues.__avatar_files.findIndex(
      (f) => f.id === fileId
    );
    if (fileIndex !== -1) {
      this.entityValues.__avatar_files[fileIndex].ticket = outcome.ticket;
    }
  }

  avatarDelete() {
    // Limpiar preview URL
    if (this.avatarPreviewUrl) {
      URL.revokeObjectURL(this.avatarPreviewUrl);
      this.avatarPreviewUrl = null;
    }

    const existingFile = this.entityValues.__avatar_files.find(
      (f) => f.__status__ === "original"
    );
    if (existingFile) {
      existingFile.__status__ = "original-deleted";
    }
    this.entityValues.__avatar_files = this.entityValues.__avatar_files.filter(
      (f) => f.__status__ !== "added"
    );
    this.entityValues.avatar = "";
  }

  getActiveValue(): boolean {
    return this.entityValues.active;
  }

  setActiveValue(value: boolean) {
    this.entityValues.active = value;
  }

  getCellphoneValue(): string {
    return this.entityValues.cellphone;
  }

  setCellphoneValue(value: string) {
    this.entityValues.cellphone = value;
  }

  getTwoFaseAuthValue(): boolean {
    return this.entityValues.twoFaseAuth;
  }

  setTwoFaseAuthValue(value: boolean) {
    this.entityValues.twoFaseAuth = value;
  }

  getIdCardTypeValue(): IdCardType | "" {
    return this.entityValues.idCardType;
  }

  setIdCardTypeValue(value: IdCardType | "") {
    this.entityValues.idCardType = value;
  }

  getIdCardTypeOptions() {
    return this.langStore.IdCardTypeOptions;
  }

  getIdCardValue(): string {
    return this.entityValues.idCard;
  }

  setIdCardValue(value: string) {
    this.entityValues.idCard = value;
  }

  getGenderValue(): Gender | "" {
    return this.entityValues.gender;
  }

  setGenderValue(value: Gender | "") {
    this.entityValues.gender = value;
  }

  getGenderOptions() {
    return this.langStore.GenderOptions;
  }

  *fileUpload(file: File): any {
    const data = yield apiUploadFile(true, file, this.rootStore.activityStore);
    // Una subida cancelada vuelve sin datos.
    return data?.singleUpload?.ticket;
  }

  *uploadTicket(file: File): any {
    // No tira: una subida que falla es un resultado que el campo tiene que
    // mostrar, no una excepción que se pierde en la consola.
    try {
      const ticket = yield this.fileUpload(file);
      // Sin ticket y sin error es un pedido abortado: el botón Cancelar.
      return ticket ? { ticket } : { cancelled: true };
    } catch (error) {
      return {
        reason: uploadFailureReason(error, this.langStore.uploadConnectionLost),
      };
    }
  }

  setFieldError(field: string, fieldLabel: string, message: string) {
    this.errors = {
      ...this.errors,
      [field]: { message, fieldLabel },
    } as UserErrorsType;
  }

  clearFieldError(field: string) {
    if (!(field in this.errors)) return;
    const errors: Record<string, unknown> = { ...this.errors };
    delete errors[field];
    this.errors = errors as UserErrorsType;
  }

  *fileDownload(props: any, fieldName: string): any {
    // En el modal anidado (subentidad editada desde el parent) el instance
    // no se carga (loadValuesFromRow): el id sale de los values de la fila.
    // Sin el fallback, la URL iba con instanceId=undefined → 404 silencioso.
    const instanceId = this.userInstance?.id ?? this.entityValues.id;
    yield downloadWithAuth(
      {
        entityName: "User",
        fieldName: fieldName,
        instanceId,
        fileName: props.name,
      },
      undefined,
      this.rootStore.activityStore
    );
  }

  openPdfViewer(url: string, title: string) {
    this.pdfViewerUrl = url;
    this.pdfViewerTitle = title;
    this.pdfViewerOpen = true;
  }

  closePdfViewer() {
    if (this.pdfViewerUrl) {
      window.URL.revokeObjectURL(this.pdfViewerUrl);
    }
    this.pdfViewerOpen = false;
    this.pdfViewerUrl = "";
    this.pdfViewerTitle = "";
  }

  *viewFilePdf(props: any, fieldName: string): any {
    try {
      // Mismo fallback que fileDownload: en el modal anidado el instance
      // no se carga y el id viene de los values de la fila.
      const instanceId = this.userInstance?.id ?? this.entityValues.id;
      const blob = yield fetchFileBlob({
        entityName: "User",
        fieldName: fieldName,
        instanceId,
        fileName: props.name,
      });
      if (!blob) return;
      this.openPdfViewer(window.URL.createObjectURL(blob), props.name);
    } catch (error) {
      console.error("Error loading PDF:", error);
    }
  }

  addNewRole(notFound: string | undefined, callerFieldName?: string) {
    this.isWaitingForCallback = true;
    this.callbackReturnPath = window.location.pathname;
    // Protect parent store in registry from destruction during navigation
    const parentStore = this.rootStore.storeRegistry.getStoreIfExists(
      this.callbackReturnPath
    );
    if (parentStore && parentStore !== this) {
      parentStore.isWaitingForCallback = true;
    }
    const callbackParams: CallbackDataParams = {
      storeId: this.callbackReturnPath,
      callerData: {
        entityName: "User",
        fieldName: callerFieldName || "",
        isMulti: true,
        notFound: notFound,
      },
    };

    this.rootStore.router.goTo(
      "/role/create",
      undefined,
      undefined,
      { callbackParams, initialDisplayId: notFound },
      this.breadcrumbLabel
    );
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.userInstance = null;
    this.entityValues = UserValuesInit();
    this.cardState = {
      null: { open: true, errors: [] },
      main: { open: true, errors: [] },
      details: { open: true, errors: [] },
    };
    this.roleSearchValue = "";
    this.roleOptions = [];
    if (this.avatarPreviewUrl) {
      URL.revokeObjectURL(this.avatarPreviewUrl);
      this.avatarPreviewUrl = null;
    }
  }

  clearErrors() {
    Object.keys(this.cardState).forEach((key) => {
      this.cardState[key].errors = [];
    });
  }

  validateData(values: UserValuesType): UserErrorsType {
    const errors: UserErrorsType = {};
    this.clearErrors();
    values.name = values.name.trim();
    if (values.name === "") {
      errors.name = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.UserNameLabel,
      };
      this.setCardError("main", errors.name.message);
    }
    values.email = values.email.trim();
    if (values.email === "") {
      errors.email = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.UserEmailLabel,
      };
      this.setCardError("main", errors.email.message);
    }

    if (!values.language) {
      errors.language = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.UserLanguageLabel,
      };
      this.setCardError("main", errors.language.message);
    }
    {
      const pendingUpload = values.__avatar_files.find(
        (f: any) => addedStatusList.includes(f.__status__) && !f.ticket
      );
      if (pendingUpload) {
        errors.avatar = {
          message: this.langStore.uploadNotFinishedMessage(pendingUpload.name),
          fieldLabel: this.langStore.UserAvatarLabel,
        };
        this.setCardError("main", errors.avatar.message);
      }
    }

    if (isNil(values.active)) {
      errors.active = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.UserActiveLabel,
      };
      this.setCardError("main", errors.active.message);
    }
    if (
      values.idCard &&
      values.idCardType &&
      ["dni"].includes(values.idCardType)
    ) {
      if (
        !["999999999", "99999999", "9999999"].some((m) =>
          validateMask(values.idCard, { mask: m })
        )
      ) {
        errors.idCard = {
          message: this.langStore.maskedField("999999999 | 99999999 | 9999999"),
          fieldLabel: this.langStore.UserIdCardLabel,
        };
      }
    } else if (
      values.idCard &&
      values.idCardType &&
      ["cuitCuil"].includes(values.idCardType)
    ) {
      if (
        !["99-99999999-9", "99-9999999-9"].some((m) =>
          validateMask(values.idCard, { mask: m })
        )
      ) {
        errors.idCard = {
          message: this.langStore.maskedField("99-99999999-9 | 99-9999999-9"),
          fieldLabel: this.langStore.UserIdCardLabel,
        };
      }
    }

    // Custom validators (registrados desde customValidations.ts). Se corren
    // siempre, aunque la entidad no tenga validaciones de campo generadas.
    const customValidationResult = runCustomValidators(
      this.customValidators,
      values,
      this.langStore,
      this.rootStore.referenceCacheStore,
      this.originalSnapshot ?? undefined
    );
    Object.assign(errors, customValidationResult.errors);
    if (customValidationResult.cardErrors) {
      for (const [cardId, messages] of Object.entries(
        customValidationResult.cardErrors
      )) {
        messages.forEach((msg) => this.setCardError(cardId, msg));
      }
    }

    this.setNavigationLinks();
    return errors;
  }

  // Avisos de los validators custom: ver collectCustomWarnings en validation/types.ts.
  get warnings(): Record<string, { message: string; fieldLabel: string }> {
    return collectCustomWarnings(
      this.customValidators,
      { ...this.entityValues },
      this.langStore,
      this.rootStore.referenceCacheStore,
      this.originalSnapshot ?? undefined,
      "UserUpdateStore"
    );
  }

  close(returnData?: { resultId?: string; cancelled?: boolean }) {
    if (this.closeHandler) {
      this.closeHandler(returnData);
      return;
    }

    if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
      return;
    }

    if (this.isDrawerMode) {
      // In drawer mode, close or pop the drawer instead of navigating
      if (this.rootStore.drawerViewStack.length > 1) {
        this.rootStore.popDrawerView();
      } else {
        this.rootStore.closeDrawerView();
      }
      return;
    }

    if (this.callbackReturnPath) {
      // Sync breadcrumbs to return path (truncates stack instead of clearing)
      this.rootStore.syncBreadcrumbsWithBrowser(this.callbackReturnPath);

      // Navigate back with callback data (include original callerData)
      // Use JSON parse/stringify to ensure the state is structurally clonable for History API
      const callbackParams: CallbackDataParams = JSON.parse(
        JSON.stringify({
          storeId: this.callbackReturnPath,
          callerData: this.callbackParams?.callerData,
          returnData: returnData || { cancelled: true },
        })
      );
      this.rootStore.router.replace(this.callbackReturnPath, {
        callbackParams,
      });
    } else {
      this.rootStore.router.goBack();
    }
  }

  get pendingChanges(): boolean {
    // Guardado pero desactualizado: cuenta lo escrito desde que se entró en ese
    // estado (la foto, y el vigía para lo que la foto no cubre), no las filas y
    // los archivos que ya se guardaron.
    if (this.savedButOutdated)
      return this._ownPendingChanges || this.editedWhileOutdated;
    return this._ownPendingChanges;
  }

  hasUnsavedChanges(): boolean {
    return this.pendingChanges;
  }

  // Public interface for navigation blocking system
  get hasPendingChanges(): boolean {
    return this.pendingChanges;
  }

  /**
   * Computes a normalized snapshot for change detection.
   * - Simple fields: direct value
   * - Single refs: normalized to ID only
   * - Multi refs: normalized to sorted array of IDs
   */
  private computeSnapshot(): Record<string, any> {
    return {
      name: this.entityValues.name,
      email: this.entityValues.email,
      language: this.entityValues.language,
      avatar: this.entityValues.avatar,
      active: this.entityValues.active,
      cellphone: this.entityValues.cellphone,
      twoFaseAuth: this.entityValues.twoFaseAuth,
      idCardType: this.entityValues.idCardType,
      idCard: this.entityValues.idCard,
      gender: this.entityValues.gender,
      role: this.entityValues.showRole?.id || null,
    };
  }

  private setupChangeDetection() {
    // Use normalized snapshot with deep equality comparison
    this.stopReaction = reaction(
      () => this.computeSnapshot(),
      (snapshot) => {
        const hasChanges = this.originalSnapshot
          ? !_.isEqual(snapshot, this.originalSnapshot)
          : false;

        if (this._ownPendingChanges !== hasChanges) {
          this._ownPendingChanges = hasChanges;
        }
      },
      { fireImmediately: false, equals: (a, b) => _.isEqual(a, b) }
    );
  }

  private saveOriginalState() {
    this.originalSnapshot = this.computeSnapshot();
    this._ownPendingChanges = false;
  }

  resetPendingChanges() {
    this._ownPendingChanges = false;
  }

  getChangedFields(): string[] {
    if (!this.pendingChanges) return [];

    const changed: string[] = [];

    if (this._ownPendingChanges && this.originalSnapshot) {
      const current = this.computeSnapshot();

      Object.keys(current).forEach((key) => {
        if (!_.isEqual(current[key], this.originalSnapshot![key])) {
          changed.push(key);
        }
      });
    }

    return changed;
  }

  *saveUser(): any {
    // Un segundo Guardar mientras corre el primero no hace nada: el primero
    // cambia la versión del registro, y el segundo fallaría con RECORD_MODIFIED
    // aunque los datos ya estén guardados. Tampoco con el formulario
    // guardado pero desactualizado: no se guarda desde un estado viejo.
    if (this.saveDisabled) return;
    this.errors = { ...this.validateData(this.entityValues) };
    if (Object.keys(this.errors).length === 0) {
      this.closeValidationErrorsDialog();

      // Guardar datos del avatar anterior para invalidar caché después
      const previousAvatarName = this.userInstance?.avatar_data?.name;
      const entityId = this.userInstance?.id;

      // El id de ESTE guardado. Si falla antes de llegar al server (al armar el
      // pedido), no queda el de uno anterior que haga correr la relectura y el
      // cierre como si éste hubiera salido bien.
      this.savedId = null;
      // Mientras se guarda, la capa no deja editar. Si el formulario cambia igual
      // (algún camino programático), lo que se ve ya no es lo que se guardó, y no
      // se concilia nada: queda guardado pero desactualizado, como si la relectura
      // fallara. La versión nueva, que _save toma de la respuesta, no cuenta.
      let changedWhileSaving = false;
      const stopWatching = reaction(
        () => ({ ...toJS(this.entityValues), version_: undefined }),
        () => {
          changedWhileSaving = true;
        },
        { equals: (a, b) => _.isEqual(a, b) }
      );
      // Desde acá hasta que termina (también la relectura), el formulario está
      // bloqueado por su capa. Pase lo que pase (también si el manejo del error
      // tira), el finally de afuera lo libera.
      this.isSaving = true;
      try {
        try {
          this.status = "processing";
          yield this._save();
        } catch (error) {
          this.status = "error";

          if (error instanceof Error) {
            const apiError = getApiError(error, this.langStore);
            this.showErrorDialog(
              apiError.code,
              apiError.title,
              apiError.message
            );
          } else {
            this.showErrorDialog(this.langStore.serverError, undefined);
          }
        } finally {
          stopWatching();
        }

        if (!this.savedId) {
          // Sin guardado exitoso (falló, o no había nada que guardar): Guardar se
          // vuelve a habilitar.
          if (this.isProcessing) this.status = "ok";
        } else {
          try {
            // El formulario queda con el registro del server, no con lo que mandó:
            // las filas nuevas toman su id, los archivos quedan guardados (no
            // "added", que se volverían a mandar) y version_ es el que quedó. Si el
            // formulario sigue abierto (volver atrás no navega, por ejemplo en una
            // pestaña nueva), el próximo guardado parte de ahí. Si no se puede poner
            // al día (cambió mientras se guardaba, o la relectura falla), el guardado
            // ya está hecho, pero lo que se ve no es lo que hay: queda guardado pero
            // desactualizado, con Guardar deshabilitado y el aviso con Recargar, y
            // no se cierra.
            if (changedWhileSaving) {
              this.becomeOutdated();
            } else {
              try {
                yield this.loadUserById(this.savedId);
              } catch (error) {
                console.warn(
                  "[UserUpdateStore] saved, but reloading the record failed:",
                  error
                );
                this.becomeOutdated();
              }
            }
            if (!this.savedButOutdated) {
              this.resetPendingChanges();
              this.saveOriginalState();
            }

            // Limpiar preview URL ya que la imagen está en el servidor
            if (this.avatarPreviewUrl) {
              URL.revokeObjectURL(this.avatarPreviewUrl);
              this.avatarPreviewUrl = null;
            }

            // Invalidar caché de imagen del avatar anterior
            if (previousAvatarName && entityId) {
              imageCache.removeFromCache(
                "User",
                "avatar",
                entityId,
                previousAvatarName
              );
            }

            // Si actualizamos el usuario actual, refrescar authStore.me
            if (this.userInstance?.id === this.rootStore.authStore.me?.id) {
              yield this.rootStore.authStore.getMe();
            }
          } finally {
            // Hasta acá sigue guardando (isProcessing): Guardar deshabilitado, y un
            // segundo guardado no corre mientras se relee.
            this.status = "ok";
          }
          if (!this.savedButOutdated)
            this.close({ resultId: this.savedId, cancelled: false });
        }
      } finally {
        // Pase lo que pase (también si el manejo del error tira), el formulario
        // se libera: la capa se cierra.
        this.isSaving = false;
      }
    } else {
      this.showValidationErrorsDialog();
    }
  }

  *cancelUser(): any {
    if (this.pendingChanges) {
      this.showConfirmDialog(
        this.langStore.confirmCancelTitle,
        this.langStore.exitWithoutSaving,
        this.executeCancelAction.bind(this)
      );
      return;
    }
    yield this.executeCancelAction();
  }

  executeCancelAction(): void {
    this.cancelled = true;
    this._ownPendingChanges = false;

    this.close({ cancelled: true });
  }

  getState(): InitializeParams {
    return {
      id: this.entityValues.id,
      uiState: {
        cardState: this.cardState,
      },
    };
  }

  loadValuesFromRow(row: any) {
    const values = getUserValues(row, this.langStore);
    // Filas nuevas de la grilla traen SOLO las columnas visibles: los campos
    // ausentes (undefined) caen al init — sin esto, validaciones tipo
    // values.x.trim() explotan y los inputs quedan uncontrolled.
    const init = UserValuesInit();
    for (const k of Object.keys(init) as (keyof typeof init)[]) {
      if ((values as any)[k] === undefined)
        (values as any)[k] = (init as any)[k];
    }
    this.entityValues = values;
    this.saveOriginalState();
  }

  *_save(): any {
    if (this.userInstance) {
      const variables = getUserUpdateVariables(this.entityValues);
      this.savedId = null;
      const api = yield* getApi(
        this.rootStore.authStore,
        this.rootStore.activityStore
      );
      const result = yield api.userUpdate({
        ...variables,
        idempotencyKey_: this.idempotencyKeyFor(variables),
      });
      this.savedId = result.userUpdate.id;
      // La versión nueva la da el server: si el formulario sigue abierto (volver
      // atrás no navega, por ejemplo en una pestaña nueva), el próximo guardado
      // la manda y no choca consigo mismo.
      this.entityValues.version_ = result.userUpdate.version_;
      this.saveAttempt = null;
      this.userInstance = result.userUpdate as User;
    }
  }

  *process(fn: AsyncFunction) {
    try {
      this.status = "processing";
      yield fn();
      this.status = "ok";
    } catch (error) {
      // El estado primero: si el diálogo fallara, Guardar no queda trabado.
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        this.showErrorDialog(apiError.code, apiError.title, apiError.message);
      } else {
        this.showErrorDialog(this.langStore.serverError, undefined);
      }
    }
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const idToLoad = params?.id || params?.duplicateFromId;

      if (idToLoad) {
        yield this.loadUserById(idToLoad);

        if (params?.duplicateFromId) {
          this.entityValues.id = "";
        }
      }

      if (params?.callbackData?.filter) {
        Object.assign(this.entityValues, params.callbackData.filter);
      }

      if (params?.uiState?.cardState) {
        Object.assign(this.cardState, params.uiState.cardState);
      }

      if (params?.preloadedData) {
        Object.assign(this.entityValues, params.preloadedData);
      }

      yield this.loadRoleValues();
      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
